import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { X, ShoppingCart, Copy, Check, Beef, Milk, Wheat, Apple, Droplet, Sparkles, Mail, MessageCircle } from 'lucide-react';
import { buildSmartShoppingList } from './shoppingListUtils';

const CATEGORY_ICON = {
  'Protéines': Beef,
  'Produits laitiers': Milk,
  'Glucides': Wheat,
  'Légumes & fruits': Apple,
  'Lipides & oléagineux': Droplet,
  'Épices & divers': Sparkles,
  'Autres': ShoppingCart,
};

/**
 * SmartShoppingListModal — Liste de courses "à la carte" (16/09/2026) : générée à partir
 * d'ingrédients en texte libre (recettes perso, repas du calendrier), catégorisée et avec
 * quantités fusionnées. Réutilisable partout dans l'app.
 */
export const SmartShoppingListModal = ({ rawIngredients, sourceLabel, en, onClose }) => {
  const grouped = useMemo(() => buildSmartShoppingList(rawIngredients), [rawIngredients]);
  const [checked, setChecked] = useState(new Set());
  const totalItems = Object.values(grouped).reduce((n, arr) => n + arr.length, 0);

  const toggle = (key) => setChecked((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const buildText = () => {
    const lines = [];
    Object.entries(grouped).forEach(([cat, items]) => {
      lines.push(`\n${cat.toUpperCase()}`);
      items.forEach((it) => lines.push(`- ${it.name}${it.qtyLabel ? ' : ' + it.qtyLabel : ''}`));
    });
    return lines.join('\n').trim();
  };

  const copyText = () => {
    const text = buildText();
    if (!navigator.clipboard) { toast.error(en ? 'Copy failed' : 'Copie impossible'); return; }
    navigator.clipboard.writeText(text).then(
      () => toast.success(en ? 'Shopping list copied' : 'Liste copiée dans le presse-papiers'),
      () => toast.error(en ? 'Copy failed' : 'Copie impossible'),
    );
  };

  const sendByEmail = () => {
    const subject = en ? 'My Body-Up shopping list' : 'Ma liste de courses Body-Up';
    const url = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildText())}`;
    window.location.href = url;
  };

  const sendBySms = () => {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const sep = isIOS ? '&' : '?';
    window.location.href = `sms:${sep}body=${encodeURIComponent(buildText())}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[230] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="smart-shopping-list-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 max-h-[85vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-primary" /> {en ? 'My shopping list' : 'Ma liste de courses'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="smart-shopping-list-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-zinc-500 mb-4" data-testid="smart-shopping-list-source">{sourceLabel} · {totalItems} {en ? 'ingredients' : 'ingrédients'}</p>

        {totalItems === 0 ? (
          <p className="text-center text-zinc-500 py-10 text-sm" data-testid="smart-shopping-list-empty">{en ? 'No ingredient found.' : 'Aucun ingrédient trouvé.'}</p>
        ) : (
          <div className="space-y-5 mb-5" data-testid="smart-shopping-list-items">
            {Object.entries(grouped).map(([cat, items]) => {
              const Icon = CATEGORY_ICON[cat] || ShoppingCart;
              return (
                <div key={cat}>
                  <p className="text-[11px] font-bold text-primary uppercase tracking-wider mb-2 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {cat}</p>
                  <ul className="space-y-1.5">
                    {items.map((it) => {
                      const key = `${cat}-${it.name}`;
                      const isChecked = checked.has(key);
                      return (
                        <li key={key}>
                          <button onClick={() => toggle(key)} className="w-full flex items-center gap-2.5 text-left" data-testid={`smart-shopping-item-${key}`}>
                            <span className={`shrink-0 w-[18px] h-[18px] rounded-full border flex items-center justify-center transition-colors ${isChecked ? 'bg-primary border-primary' : 'border-zinc-600'}`}>
                              {isChecked && <Check className="w-3 h-3 text-white" />}
                            </span>
                            <span className={`flex-1 text-sm capitalize ${isChecked ? 'text-zinc-500 line-through' : 'text-zinc-200'}`}>{it.name}</span>
                            {it.qtyLabel && <span className="text-xs text-zinc-500 shrink-0">{it.qtyLabel}</span>}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        <button
          onClick={copyText}
          disabled={totalItems === 0}
          className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid="smart-shopping-list-copy-btn"
        >
          <Copy className="w-4 h-4" /> {en ? 'Copy list' : 'Copier la liste'}
        </button>
        <div className="flex items-center gap-2 mt-2.5">
          <button
            onClick={sendByEmail}
            disabled={totalItems === 0}
            className="flex-1 py-2.5 rounded-xl font-semibold text-sm text-zinc-200 bg-white/5 border border-white/10 hover:border-primary/40 hover:text-white transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40"
            data-testid="smart-shopping-list-email-btn"
          >
            <Mail className="w-3.5 h-3.5" /> {en ? 'Email' : 'Par email'}
          </button>
          <button
            onClick={sendBySms}
            disabled={totalItems === 0}
            className="flex-1 py-2.5 rounded-xl font-semibold text-sm text-zinc-200 bg-white/5 border border-white/10 hover:border-primary/40 hover:text-white transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40"
            data-testid="smart-shopping-list-sms-btn"
          >
            <MessageCircle className="w-3.5 h-3.5" /> SMS
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SmartShoppingListModal;
